import { Dayjs } from "dayjs";
import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EventLabellingModelStatus,
  EventsForReview,
  TimelineFeature,
  useGetEventsForReviewQuery,
} from "src/store/api/generatedApi";
import { useSupersededReviewEventsResponse } from "../manualReview/hooks/useSupersededReviewEventsResponse";
import { useTimelineExclusions } from "../manualReview/hooks/useTimelineExclusions";
import { reviewEventsQueryArgs } from "../manualReview/utils/reviewEventsQueryArgs";
import {
  TIMELINE_FEATURES,
  TimelineExclusionPeriodIds,
} from "../manualReview/utils/timelineExclusions";

export const TARGETED_REVIEW_OMITTED_MODEL_STATUSES: readonly EventLabellingModelStatus[] =
  ["passive"];

const isPeriodIds = (ids: unknown): ids is number[] =>
  Array.isArray(ids) &&
  ids.every((id: unknown) => typeof id === "number" && Number.isFinite(id));

const acknowledgedExclusions = (
  response: EventsForReview | undefined
): TimelineExclusionPeriodIds | undefined => {
  const applied = response?.applied_exclusions;
  if (
    response?.exclusion_mode_applied !== "server_defaults" ||
    !applied ||
    !TIMELINE_FEATURES.every((feature) => isPeriodIds(applied[feature]))
  ) {
    return undefined;
  }
  return {
    all: new Set(applied.all),
    material: new Set(applied.material),
    timing: new Set(applied.timing),
  };
};

type RequestMode = "server_defaults" | "explicit";

export const useTargetedReviewEvents = ({
  tenantName,
  location,
  windowStart,
  windowEnd,
  includeVerified,
  skip,
}: {
  tenantName: string;
  location: string;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  includeVerified: boolean;
  skip: boolean;
}) => {
  const windowKey = `${tenantName}|${location}|${windowStart.toISOString()}|${windowEnd.toISOString()}`;
  const exclusions = useTimelineExclusions({ windowKey });
  const { applyResolved } = exclusions;
  const [requestState, setRequestState] = useState<{
    windowKey: string;
    mode: RequestMode;
    acceptedResponse: EventsForReview | undefined;
  }>({ windowKey, mode: "server_defaults", acceptedResponse: undefined });
  const mode =
    requestState.windowKey === windowKey
      ? requestState.mode
      : "server_defaults";
  const useExplicitExclusions = mode === "explicit";

  const query = useGetEventsForReviewQuery(
    useExplicitExclusions
      ? reviewEventsQueryArgs({
          tenantName,
          location,
          windowStart,
          windowEnd,
          excludedPeriodIds: exclusions.excludedPeriodIds,
          excludeMaterialPeriodIds: exclusions.materialExcludedPeriodIds,
          excludeTimingPeriodIds: exclusions.timingExcludedPeriodIds,
          includeVerified,
          omittedModelStatuses: TARGETED_REVIEW_OMITTED_MODEL_STATUSES,
        })
      : {
          tenantName,
          location,
          windowStart: windowStart.toISOString(),
          windowEnd: windowEnd.toISOString(),
          includeVerified,
          exclusionMode: "server_defaults",
          omittedModelStatuses: [...TARGETED_REVIEW_OMITTED_MODEL_STATUSES],
        },
    { skip }
  );
  const acknowledged = useMemo(
    () => acknowledgedExclusions(query.currentData),
    [query.currentData]
  );
  const currentIds = useMemo(
    () => ({
      all: exclusions.excludedPeriodIds,
      material: exclusions.materialExcludedPeriodIds,
      timing: exclusions.timingExcludedPeriodIds,
    }),
    [
      exclusions.excludedPeriodIds,
      exclusions.materialExcludedPeriodIds,
      exclusions.timingExcludedPeriodIds,
    ]
  );
  const hasRetainedExclusions =
    acknowledged &&
    TIMELINE_FEATURES.some((feature) =>
      [...currentIds[feature]].some((id) => !acknowledged[feature].has(id))
    );

  useEffect(() => {
    if (!query.currentData || (!useExplicitExclusions && !acknowledged)) return;
    applyResolved(acknowledged);
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setRequestState((previous) => {
      const nextMode = hasRetainedExclusions ? "explicit" : mode;
      const acceptedResponse = hasRetainedExclusions
        ? undefined
        : query.currentData;
      return previous.windowKey === windowKey &&
        previous.mode === nextMode &&
        previous.acceptedResponse === acceptedResponse
        ? previous
        : { windowKey, mode: nextMode, acceptedResponse };
    });
  }, [
    query.currentData,
    applyResolved,
    useExplicitExclusions,
    acknowledged,
    hasRetainedExclusions,
    mode,
    windowKey,
  ]);

  const setFeature = useCallback(
    (feature: TimelineFeature, action: SetStateAction<Set<number>>) => {
      setRequestState((previous) =>
        previous.windowKey === windowKey && previous.mode === "explicit"
          ? previous
          : { windowKey, mode: "explicit", acceptedResponse: undefined }
      );
      const setters = {
        all: exclusions.setExcludedPeriodIds,
        material: exclusions.setMaterialExcludedPeriodIds,
        timing: exclusions.setTimingExcludedPeriodIds,
      };
      setters[feature](action);
    },
    [
      windowKey,
      exclusions.setExcludedPeriodIds,
      exclusions.setMaterialExcludedPeriodIds,
      exclusions.setTimingExcludedPeriodIds,
    ]
  );
  const editableExclusions = useMemo(
    () => ({
      ...exclusions,
      setExcludedPeriodIds: (action: SetStateAction<Set<number>>) => {
        setFeature("all", action);
      },
      setMaterialExcludedPeriodIds: (action: SetStateAction<Set<number>>) => {
        setFeature("material", action);
      },
      setTimingExcludedPeriodIds: (action: SetStateAction<Set<number>>) => {
        setFeature("timing", action);
      },
    }),
    [exclusions, setFeature]
  );

  const matchesAcknowledgement =
    acknowledged &&
    TIMELINE_FEATURES.every(
      (feature) =>
        currentIds[feature].size === acknowledged[feature].size &&
        [...currentIds[feature]].every((id) => acknowledged[feature].has(id))
    );
  const resolvedResponse =
    exclusions.resolved &&
    requestState.windowKey === windowKey &&
    requestState.acceptedResponse === query.currentData &&
    (useExplicitExclusions || matchesAcknowledgement)
      ? query.currentData
      : undefined;
  const isError =
    query.isError ||
    (!skip &&
      !useExplicitExclusions &&
      Boolean(query.currentData) &&
      !acknowledged);
  const supersededResponse = useSupersededReviewEventsResponse({
    windowKey,
    resolvedResponse,
  });

  return {
    resolvedResponse,
    supersededResponse,
    exclusions: editableExclusions,
    isError,
    isFetching: query.isFetching || (!skip && !isError && !resolvedResponse),
  };
};
